import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, HowItWorks, BenefitsGrid, FAQSection, CTASection } from '../../components/PageTemplate';
import { 
  DollarSign, Clock, Shield, Camera, BarChart3, Wrench, Users, 
  MessageCircle, FileText, Zap, CheckCircle, Building2, ArrowRight, AlertTriangle 
} from 'lucide-react';

export default function LandlordsOverview() {
  const steps = [
    {
      title: 'Add Your Property',
      description: 'Enter property details, upload photos, and set your rent. Our wizard guides you through compliance requirements.'
    },
    {
      title: 'We Handle Compliance',
      description: 'Upload certificates (EPC, Gas Safe, EICR) - we track expiries and remind you before deadlines.'
    },
    {
      title: 'Receive Applications',
      description: 'Tenants apply with verified profiles. We handle referencing, affordability checks, and Right to Rent.'
    },
    {
      title: 'Automated Tenancy Setup',
      description: 'Digital contracts, e-signatures, deposit protection - all handled automatically.'
    },
    {
      title: 'Ongoing Management',
      description: 'Rent collection, maintenance, inspections, renewals - we manage it all with full transparency.'
    },
    {
      title: 'Clear Reporting',
      description: 'Real-time dashboard, monthly statements, complete audit trail of every action.'
    }
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: '6% Full Management Fee',
      description: 'Lower than traditional agents. No hidden fees, no setup costs, no exit fees.',
      highlight: true
    },
    {
      icon: Clock,
      title: 'Faster Letting',
      description: 'Properties go live on major portals. AI-powered tenant matching reduces void periods.'
    },
    {
      icon: Shield,
      title: 'Compliance Automation',
      description: 'We track every certificate, send reminders, and ensure you\'re always compliant.'
    },
    {
      icon: Camera,
      title: 'Digital Inspections',
      description: 'Video walkthroughs, 3D tours, automated condition comparisons between inspections.'
    },
    {
      icon: Wrench,
      title: 'Smart Repairs',
      description: 'Tenants report issues in-app. Auto-approve small repairs, get quotes for larger ones.'
    },
    {
      icon: Users,
      title: 'Vetted Contractors',
      description: 'Jobs go to our network of verified contractors. First-to-accept model means faster fixes.'
    },
    {
      icon: MessageCircle,
      title: 'Clear Communication',
      description: 'All messages in one place. No more chasing emails or phone calls.'
    },
    {
      icon: BarChart3,
      title: 'Full Transparency',
      description: 'See every transaction, every action, every decision. Complete audit trail.'
    },
    {
      icon: FileText,
      title: 'Digital Documents',
      description: 'Contracts, certificates, inventories - all stored securely and accessible anytime.'
    }
  ];

  const faqs = [
    {
      question: 'What does the 6% management fee include?',
      answer: 'Everything: tenant finding, referencing, contract generation, rent collection, maintenance coordination, inspections, deposit handling, compliance tracking, and 24/7 support. No additional charges.'
    },
    {
      question: 'How long does it take to list my property?',
      answer: 'Most properties go live within 24-48 hours. Upload your details, photos, and certificates - we handle the rest.'
    },
    {
      question: 'What happens if a tenant doesn\'t pay rent?',
      answer: 'Our system automatically chases arrears with escalating communications. We provide clear records for any legal proceedings if needed.'
    },
    {
      question: 'Can I manage multiple properties?',
      answer: 'Yes. Our dashboard is designed for portfolio landlords - manage all your properties from one screen.'
    },
    {
      question: 'What jurisdictions do you cover?',
      answer: 'We operate across England, Scotland, and Wales, with jurisdiction-specific compliance rules built in.'
    }
  ];

  const subPages = [
    { href: '/landlords/listing', title: 'Listing & Marketing', description: 'Portal syndication, add-ons, photos', icon: Camera },
    { href: '/landlords/compliance', title: 'Compliance & Safety', description: 'Certificates, reminders, audit trail', icon: Shield },
    { href: '/landlords/tenancy', title: 'Tenancy & Deposits', description: 'Contracts, signing, deposit protection', icon: FileText },
    { href: '/landlords/maintenance', title: 'Maintenance & Repairs', description: 'Work orders, approvals, contractors', icon: Wrench },
    { href: '/landlords/pricing', title: 'Pricing', description: 'Packages and add-on services', icon: DollarSign },
    { href: '/landlords/faqs', title: 'FAQs', description: 'Common landlord questions', icon: MessageCircle },
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="FOR LANDLORDS"
        title="Property Management,"
        subtitle="Without the Headaches"
        description="LetWise handles everything from listing to maintenance at just 6% - lower than traditional agents, with better technology and full transparency."
        primaryCTA={{ href: '/register', label: 'Get Started Free' }}
        secondaryCTA={{ href: '/landlords/pricing', label: 'View Pricing' }}
        gradient="from-green-50 via-white to-slate-50"
      />

      {/* RRA 2025 Alert Banner */}
      <section className="py-4 sm:py-6 bg-gradient-to-r from-emerald-900 to-teal-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Link 
            to="/renters-rights-act"
            className="flex items-center justify-between gap-4 text-white hover:opacity-90 transition-opacity group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-medium text-amber-400 uppercase tracking-wide">Important Update</span>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                </div>
                <p className="font-bold text-lg">Renters&apos; Rights Act 2025 — Section 21 Ends May 2026</p>
                <p className="text-emerald-200 text-sm hidden sm:block">New possession grounds, pet request rights, rent increase rules. Learn how LetWise keeps you compliant.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="hidden md:block text-emerald-200 font-medium">Learn More</span>
              <ArrowRight className="w-6 h-6 text-emerald-300 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </section>

      {/* Sub-page Navigation */}
      <section className="py-8 sm:py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {subPages.map((page) => (
              <Link
                key={page.href}
                to={page.href}
                className="group p-4 rounded-xl border hover:border-primary hover:bg-primary/5 transition-all text-center"
              >
                <page.icon className="w-6 h-6 mx-auto mb-2 text-slate-400 group-hover:text-primary transition-colors" />
                <div className="font-medium text-sm group-hover:text-primary transition-colors">{page.title}</div>
                <div className="text-xs text-slate-500 mt-1 hidden sm:block">{page.description}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks title="How It Works" steps={steps} />
      
      <BenefitsGrid title="Why Landlords Choose LetWise" benefits={benefits} />

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to simplify your lettings?"
        description="Join thousands of landlords saving time and money with LetWise."
        primaryCTA={{ href: '/register', label: 'Create Free Account' }}
        secondaryCTA={{ href: '/landlords/pricing', label: 'View Pricing' }}
      />
    </PublicLayout>
  );
}
